// Keeps the signed-in user's achievement tracks current from the account
// socket, and derives the celebration queue from them: completions the user
// has not acknowledged yet. Pages at a pause moment (the library) render the
// queue; every other page holds it. The queue is state, not events: a
// completion stays unacknowledged in every snapshot until the user collects
// it, so it survives redirects, reloads, and time away.

import React, { createContext, useCallback, useEffect, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AccountServerMessageType,
  AchievementTrackProgress,
} from '@airhead-games/shared/types';
import { createLogger } from '@airhead-games/shared/utilities';
import { useAuth } from '../hooks/useAuth';
import { useAccountSocket } from '../hooks/useAccountSocket';
import {
  achievementTracksQueryKey,
  fetchAchievementTracks,
} from '../hooks/useAchievementTracks';
import { acknowledgeAchievement } from '../transport/api/protectedApi';

const logger = createLogger('AchievementContext');

/** One earned milestone waiting to be shown. */
export interface PendingCompletion {
  track: AchievementTrackProgress;
  milestone: AchievementTrackProgress['milestones'][number];
  level: number; // the 1-based level this milestone earned
}

export interface AchievementContextType {
  /** Unacknowledged completions, oldest first. */
  pending: PendingCompletion[];
  acknowledge: (completion: PendingCompletion) => void;
}

export const AchievementContext = createContext<AchievementContextType | null>(
  null
);

const processedTime = ({ milestone }: PendingCompletion): number =>
  new Date(milestone.completion?.processedAt ?? 0).getTime();

const replaceTrack = (
  tracks: AchievementTrackProgress[],
  track: AchievementTrackProgress
): AchievementTrackProgress[] =>
  tracks.some((t) => t.achievementKey === track.achievementKey)
    ? tracks.map((t) => (t.achievementKey === track.achievementKey ? track : t))
    : [...tracks, track];

export const AchievementProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { user } = useAuth();
  const userId = user?._id;
  const queryClient = useQueryClient();
  const { subscribe } = useAccountSocket();

  // Observe without fetching: the account socket writes the tracks, and this
  // observer keeps them cached while no page reads them.
  const { data: tracks } = useQuery({
    queryKey: achievementTracksQueryKey(userId),
    queryFn: fetchAchievementTracks,
    enabled: false,
  });

  useEffect(() => {
    if (!userId) return;
    const queryKey = achievementTracksQueryKey(userId);
    return subscribe((message) => {
      switch (message.type) {
        case AccountServerMessageType.SNAPSHOT:
          // Another tab signed in as someone else; the account socket
          // context switches the user, and this effect resubscribes.
          if (message.payload.userId !== userId) return;
          queryClient.setQueryData(queryKey, message.payload.achievements);
          return;
        case AccountServerMessageType.ACHIEVEMENT_PROGRESS: {
          const { track } = message.payload;
          queryClient.setQueryData<AchievementTrackProgress[]>(
            queryKey,
            (prev) => (prev ? replaceTrack(prev, track) : prev)
          );
          return;
        }
      }
    });
  }, [queryClient, subscribe, userId]);

  const pending = useMemo<PendingCompletion[]>(() => {
    if (!userId || !tracks) return [];
    return tracks
      .flatMap((track) =>
        track.milestones.flatMap((milestone, index) =>
          milestone.completion && !milestone.completion.acknowledged
            ? [{ track, milestone, level: index + 1 }]
            : []
        )
      )
      .sort((a, b) => processedTime(a) - processedTime(b));
  }, [tracks, userId]);

  const acknowledge = useCallback<AchievementContextType['acknowledge']>(
    ({ track, milestone }) => {
      const { achievementKey } = track;
      const { milestoneKey } = milestone;
      // Hide it at once; the server's track push confirms it in every tab. A
      // lost acknowledgement is not rolled back: the next snapshot shows the
      // completion once more.
      queryClient.setQueryData<AchievementTrackProgress[]>(
        achievementTracksQueryKey(userId),
        (prev) =>
          prev?.map((t) =>
            t.achievementKey !== achievementKey
              ? t
              : {
                  ...t,
                  milestones: t.milestones.map((m) =>
                    m.milestoneKey === milestoneKey && m.completion
                      ? {
                          ...m,
                          completion: { ...m.completion, acknowledged: true },
                        }
                      : m
                  ),
                }
          )
      );
      acknowledgeAchievement({ achievementKey, milestoneKey }).catch((error) =>
        logger.warn(
          `Achievement ${achievementKey}/${milestoneKey} not acknowledged`,
          error
        )
      );
    },
    [queryClient, userId]
  );

  const value = useMemo(
    () => ({ pending, acknowledge }),
    [pending, acknowledge]
  );

  return (
    <AchievementContext.Provider value={value}>
      {children}
    </AchievementContext.Provider>
  );
};
